import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico whitespace-nowrap"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// Newsletter preview principale (sezione 5).
// Mockup mobile della newsletter "Lo Scudetto è di Perugia" su iPhone in prospettiva.
const NEWSLETTER_PREVIEW =
  "/images/assets/14m6x84n_1pbmgbmw_copia-di-una-collaborazione-su-4-fronti.png";
const NEWSLETTER_LINK =
  "https://reporting.volleyballworld.com/cgi-bin/view?h+143593-2+295381+26169+143593-295381-B4D2CCBF-26169-12150868";

// Galleria sezione 7 — 9 slot (era 8: aggiunto slot finale per il vecchio newsletter
// asset). Mapping 1:1 stretto (slot i ↔ GALLERY_PHOTOS[i]).
// Slot oltre la lunghezza dell'array mostrano placeholder "Foto in arrivo".
const GALLERY_SLOTS = 9;
const GALLERY_PHOTOS = [
  // 01 — Twitter/X post Volleyball World "ITALY ARE THE WORLD CHAMPIONS"
  "/images/assets/o0asczxx_screenshot-2026-05-21-alle-14.59.31.png",
  // 02 — On-site VNL (B&W, photographer at arena)
  "/images/assets/6cbw7jp4_1.png",
  // 03 — DEM "Argento per Vale e Reka" (Beach Pro Tour)
  "/images/assets/ccip5i7i_newsletter-preview2.png",
  // 04 — DEM "Undefeated" docufilm Azzurre VNL 2025
  "/images/assets/6qao2viu_newsletter-preview3.png",
  // 05 — Key visual "Una collaborazione su 4 fronti"
  "/images/assets/1pbmgbmw_copia-di-una-collaborazione-su-4-fronti.png",
  // 06 — VNL tablet mockup "Il calendario VNL è fuori ora"
  "/images/assets/kd81c7bn_5.png",
  // 07 — On-site VNL — Egonu hi-five (B&W)
  "/images/assets/ivr3a6zr_4.png",
  // 08 — On-site VNL — Photographer dall'arena (B&W)
  "/images/assets/uech47h1_3.png",
  // 09 — Ex newsletter-preview (Instagram profile @volleyballworlditalia) — spostata in coda
  "/images/assets/3qbk4db2_2.png",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Volleyball World Italia — Asset ${String(i + 1).padStart(2, "0")} di ${GALLERY_SLOTS}`,
}));

const VolleyballWorld = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const idx = WORKS.findIndex((w) => w.to === "/works/volleyball-world-italia");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = WORKS[(idx + 1) % WORKS.length];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Volleyball World Italia", url: "/works/volleyball-world-italia" },
  ]);

  return (
    <>
      <Seo
        title="Volleyball World Italia: 121K follower, 7,16% engagement | OnSports Case"
        description="Lancio dei canali italiani Volleyball World: 121.000+ follower acquisiti, engagement rate 7,16%. OnSports Content & IP Studio, 2025."
        path="/works/volleyball-world-italia"
        image="/images/assets/3qbk4db2_2.png"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/volleyball-world-italia/hero.jpg"
        imageAlt="Volleyball World Italia — community editoriale"
        eyebrow="● Case study · Cultural Impact · Social · Content · Email"
        titleLines={["Una pagina vuota nel 2024.", "Una community da 121K."]}
        highlightWord="121K."
        subtitle="Abbiamo aperto da zero i canali italiani di Volleyball World — il braccio commerciale e media della FIVB — e li abbiamo trasformati in due anni nella più solida community editoriale italiana sul volley internazionale. Social, articoli, email, e copertura on-site di tutte le edizioni della Volleyball Nations League dal 2024 a oggi."
        meta="Dal 2024 a oggi  ·  Cliente — Volleyball World (FIVB)  ·  Categoria — Content & Community Building"
        overlayOpacity={0.55}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Volleyball World è la società che gestisce il marketing globale, i
                    diritti commerciali e i media internazionali del volley sotto l'egida
                    della FIVB. Nel 2024 ci ha affidato l'apertura e la gestione della
                    sua presenza editoriale in Italia: un mercato che è il più solido
                    d'Europa per il movimento volley, ma che fino ad allora non aveva un
                    canale ufficiale dedicato alla narrazione del volley internazionale.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Abbiamo costruito da zero una piattaforma editoriale multi-canale —
                    pagina Instagram @volleyballworlditalia, newsletter dedicate,
                    articoli di approfondimento — capace di raccontare il volley globale
                    (Volleyball Nations League, Mondiali, Champions League, atleti
                    internazionali) con uno sguardo italiano. Tutto integrato dalla
                    nostra presenza fisica negli eventi chiave: dal 2024 a oggi siamo
                    on-site a ogni edizione della Volleyball Nations League per
                    coordinare la produzione di contenuti, intercettare le storie e dare
                    voce ai protagonisti.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">Volleyball World Italia</SchedaRow>
                    <SchedaRow label="Client">Volleyball World (FIVB)</SchedaRow>
                    <SchedaRow label="Ruolo">
                      Social media management<br />
                      Editorial content<br />
                      Email marketing<br />
                      Coverage on-site VNL
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Cultural Impact · Content &amp; IP Studio
                    </SchedaRow>
                    <SchedaRow label="Canale principale">
                      Instagram @volleyballworlditalia
                    </SchedaRow>
                    <SchedaRow label="Durata">
                      Dal 2024 a oggi · in corso
                    </SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Costruire una community dove non esisteva.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Aprire un canale ufficiale di un brand globale dello sport in un
                  mercato verticale richiede di gestire una tensione: rispettare
                  l'identità internazionale del brand madre — codici visivi, tone of
                  voice, governance dei contenuti — e allo stesso tempo costruire un
                  linguaggio davvero italiano, che parli al pubblico locale del volley
                  con i riferimenti che riconosce. Non una versione tradotta della voce
                  globale, ma una voce italiana riconoscibile.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  A questo si sommava il problema strutturale di ogni canale aperto da
                  zero: nessuna audience, nessuna distribuzione organica, nessuna social
                  proof. Crescere in modo sano — non con follow/unfollow né con acquisti
                  — richiede contenuti che funzionino davvero sull'algoritmo e una
                  presenza continuativa nei momenti che contano del calendario sportivo.
                  Per questo il programma è stato costruito sulla doppia leva del lavoro
                  editoriale quotidiano e della copertura fisica degli eventi.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL PROGRAMMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-programma">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Cosa facciamo</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Quattro fronti. Una sola missione.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
                  Far crescere la pallavolo in Italia, attraverso quattro aree integrate
                  di lavoro che si alimentano a vicenda — perché ciò che produciamo in
                  un'arena della VNL diventa contenuto social, articolo editoriale e
                  newsletter nelle ore successive.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Social media management"
                body="Apertura, sviluppo e gestione editoriale di @volleyballworlditalia. Piano editoriale settimanale, produzione di reel, caroselli e post nativi, community management, attivazioni tematiche legate al calendario VNL, Mondiali e Champions. Tone of voice italiano costruito ad hoc, distinto dalla voce globale del brand."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Editorial content"
                body="Match recap, interviste, approfondimenti tecnici e storie di atleti. Articoli che raccontano il volley internazionale con uno sguardo vicino ai fan italiani, distribuiti sui canali ufficiali di Volleyball World in Italia."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Email marketing"
                body="Newsletter dedicate al pubblico italiano, con contenuti esclusivi, storytelling e attivazioni pensate per rafforzare il legame tra Volleyball World e la community italiana. Segmentazione dei flussi in base agli interessi (VNL, club, atleti internazionali)."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Coverage on-site VNL"
                body="Presenza fisica a tutte le edizioni della Volleyball Nations League dal 2024 a oggi. Coordinamento della produzione di contenuti dall'arena, interviste flash agli atleti, contenuti dietro le quinte, interazione con la fanbase italiana dal vivo durante le tappe dei tornei."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — LA NEWSLETTER IN AZIONE */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-newsletter">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Esempio editoriale</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">La newsletter, in azione.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Un esempio concreto del nostro lavoro editoriale per Volleyball World
                  Italia. Una delle newsletter inviate alla community italiana —
                  produzione completa, dall'editoriale al design, dal copy alla
                  pianificazione di invio.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll delay={0.05}>
            <div
              className="mx-auto max-w-[900px] grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12 items-center"
              data-testid="newsletter-card"
            >
              {/* Image / Preview */}
              <div className="md:col-span-2">
                <a
                  href={NEWSLETTER_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Apri la newsletter Volleyball World Italia in una nuova scheda"
                  data-testid="newsletter-preview-link"
                  data-hover="true"
                  className="block group"
                >
                  {NEWSLETTER_PREVIEW ? (
                    <div
                      className="relative w-full overflow-hidden border border-mandrogni"
                      style={{
                        aspectRatio: "4 / 5",
                        boxShadow: "0 8px 32px rgba(0, 49, 67, 0.15)",
                      }}
                    >
                      <img
                        src={NEWSLETTER_PREVIEW}
                        alt="Anteprima newsletter Volleyball World Italia"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                        draggable="false"
                      />
                    </div>
                  ) : (
                    <div
                      className="relative w-full border border-lime border-dashed bg-dodgers flex items-center justify-center"
                      style={{ aspectRatio: "4 / 5" }}
                      data-testid="newsletter-preview-placeholder"
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Anteprima newsletter in arrivo
                      </span>
                    </div>
                  )}
                </a>
              </div>

              {/* Text + CTA */}
              <div className="md:col-span-3 flex flex-col gap-5">
                <span className="font-condensed uppercase tracking-widest text-lime" style={{ fontSize: "12px" }}>
                  DEM · Volleyball World Italia
                </span>
                <h3
                  className="font-display leading-[1.05] text-galactico"
                  style={{ fontSize: "clamp(1.5rem, 2.4vw, 1.75rem)" }}
                >
                  Newsletter Volleyball World Italia
                </h3>
                <p className="text-[16px] leading-relaxed text-galactico opacity-85 max-w-[52ch]">
                  Una delle nostre DEM al pubblico italiano — editoriale, design e copy
                  curati internamente, segmentazione costruita sulla nostra base attiva
                  di iscritti.
                </p>
                <div>
                  <a
                    href={NEWSLETTER_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Apri la newsletter Volleyball World Italia in una nuova scheda"
                    data-testid="newsletter-cta"
                    className="inline-flex items-center gap-3 bg-lime text-dodgers font-condensed uppercase tracking-widest px-6 py-3 text-[14px] hover:bg-galactico hover:text-lime transition-colors"
                    data-hover="true"
                  >
                    <span>Apri la newsletter completa</span>
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — IMPATTO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Una community che cresce ogni giorno.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="121K+" label="Follower community aperta da zero nel 2024" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="7,16%" label="Engagement rate Instagram (+1,7 pp sopra benchmark settore)" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="34,1M" label="Visualizzazioni mensili Instagram organiche" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="2024→" label="Attivazione on-site VNL continua ogni edizione" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati Instagram @volleyballworlditalia aggiornati a maggio 2026. Engagement
            rate calcolato sul rapporto interazioni/follower secondo benchmark Not Just
            Analytics, con baseline di settore al 5,46%. Crescita organica
            continuativa: +1.917 follower negli ultimi 30 giorni, +66 al giorno in
            media, senza utilizzo di tecniche di follow/unfollow.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
                  Crescita organica vera
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  I 121mila follower sono stati costruiti un post alla volta, senza
                  acquisti né scorciatoie. La crescita continua mese dopo mese — +1.917
                  nelle ultime quattro settimane — è la prova che il canale ha trovato
                  il suo posto stabile dentro alla conversazione italiana sul volley.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
                  Engagement sopra benchmark
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Il 7,16% di engagement rate è significativamente sopra la media di
                  settore (5,46%) per profili comparabili. Significa che la community
                  non solo cresce, ma reagisce: like, commenti e condivisioni che
                  dimostrano un'audience davvero ingaggiata, non passiva.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
                  Programma full-stack
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Social, articoli, email e presenza dal vivo non sono quattro servizi
                  separati ma un unico sistema editoriale. La copertura on-site della
                  VNL alimenta i contenuti social del giorno dopo, la newsletter
                  trasforma le storie più forti in approfondimenti, gli articoli
                  costruiscono il patrimonio long-form del canale.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — GALLERIA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il lavoro, in immagini.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Una selezione di asset prodotti per Volleyball World Italia: social,
                  articoli editoriali, contenuti per newsletter ed eventi on-site della
                  Volleyball Nations League.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 grid-flow-row-dense gap-2"
            data-testid="gallery-grid"
          >
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              const span2 = i === 0 || i === 4; // 1° e 5° slot: 2 colonne
              const wrapperClass = span2 ? "md:col-span-2 md:row-span-2" : "";
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll
                    key={i}
                    delay={Math.min(i, 6) * 0.04}
                    className={wrapperClass}
                  >
                    <div
                      className="block w-full aspect-square relative bg-dodgers/[0.05] border border-lime flex items-center justify-center"
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Asset ${i + 1} di ${GALLERY_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Foto in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 6) * 0.04}
                  className={wrapperClass}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full aspect-square overflow-hidden relative group focus:outline-none"
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri asset ${i + 1} di ${GALLERY_SLOTS}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 8 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Client">Volleyball World (FIVB)</SchedaRow>
                <SchedaRow label="Progetto">Volleyball World Italia</SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Social media management · Editorial content<br />
                  Email marketing · Coverage on-site VNL
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Content &amp; IP Studio · Performance Marketing
                </SchedaRow>
                <SchedaRow label="Durata">Dal 2024 a oggi · in corso</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[28ch]">
              Hai un brand globale che vuole costruire una community locale vera?
              Costruiamo insieme il suo <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Lavoriamo con federazioni internazionali, leghe e property globali che
              vogliono entrare nel mercato italiano con voce propria. Apriamo canali,
              costruiamo community, sosteniamo la crescita giorno dopo giorno.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default VolleyballWorld;
